import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, Download, FileWarning, RefreshCw, Search, TriangleAlert } from 'lucide-react'
import { useGetStatementMatrix } from '../../hooks/backend/statements'
import { Input } from '../../lib/shadcn/input'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../../lib/shadcn/tooltip'
import { cn } from '../../lib/shadcn/utils'
import { Popover, PopoverTrigger, PopoverContent } from '../../lib/shadcn/popover'
import { Chip } from '../interviews/Chip'
import { FilterBar, CollapsibleBlock } from '../common/FilterBar'
import { StatementCellProvenance } from './StatementCellProvenance'
import { MonthWrites } from './MonthWrites'
import { formatZar, formatInt, formatNumber, formatDate } from '../../utils/statements'
import {
  type StatementMatrix as Matrix,
  type StatementMatrixColumn,
  type StatementMatrixRow,
  type StatementGroup,
  type StatementBasis,
  EMPTY_MATRIX,
  GROUP_LABELS,
  GROUP_ORDER,
  sectionHeading,
  isSummable,
  hasFleetTotal,
  rowTotal,
  displayValue,
  periodShortLabel,
  periodFileStem,
} from '../../utils/statementMatrix'
import { downloadXlsx, type XlsxCell, type XlsxSheet } from '../../utils/xlsx'

// Sendable is the default because it is the working set: in July it was 26
// statements against 46 that are deliberately never sent. Showing all 72 by
// default would bury the month you actually have to act on.
type GroupFilter = 'sendable' | 'all' | StatementGroup

const GROUP_FILTER_OPTIONS: { value: GroupFilter; label: string }[] = [
  { value: 'sendable', label: 'Sendable (active + new model)' },
  { value: 'new_model', label: 'New model only' },
  { value: 'active', label: 'Old model active only' },
  { value: 'offboarded', label: 'Offboarded only (not sent)' },
  { value: 'all', label: 'All statements' },
]

// Widths of the two pinned columns, in pixels. Kept as constants because the
// second column's `left` offset has to equal the first column's width exactly
// or the pinned pair drifts apart as the table scrolls.
const LABEL_W = 232
const TOTAL_W = 128

// A ratio in the Total column is not the sum of the column, so the cell says
// what it actually is. Underlined to mark it as derived rather than added.
const DERIVED_TOTAL_HINT: Record<string, string> = {
  earnings_per_shift: 'Fleet total earnings divided by fleet total shifts, not the sum of the column.',
  earnings_per_km: 'Fleet total earnings divided by fleet total kilometres, not the sum of the column.',
  net_per_km: 'Fleet net divided by fleet total kilometres, not the sum of the column.',
}

// One place decides what a number looks like on this screen: the row's display
// convention first (Payouts Due is stored negative and read positive), then the
// same formatters the single-vehicle statement tab uses.
function formatCellValue(row: StatementMatrixRow, value: number): string {
  const shown = displayValue(row, value)
  if (row.is_money) return formatZar(shown)
  return Number.isInteger(shown) ? formatInt(shown) : formatNumber(shown, 1)
}

function ColumnHeader({ column }: { column: StatementMatrixColumn }) {
  return (
    <div className="flex w-40 flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-xs font-semibold text-foreground">{column.plate}</span>
        {column.has_source_errors && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <FileWarning className="h-3.5 w-3.5 text-amber-500" />
              </span>
            </TooltipTrigger>
            <TooltipContent>At least one figure could not be sourced this month.</TooltipContent>
          </Tooltip>
        )}
      </div>
      <span className="truncate text-[11px] font-normal text-muted-foreground" title={column.owner_name}>
        {column.owner_name || 'Unnamed owner'}
      </span>
      <div className="flex flex-wrap items-center gap-1">
        {column.is_draft && <Chip tone="amber">Draft</Chip>}
        {column.do_not_send && <Chip tone="neutral">Not sent</Chip>}
        {column.issued_at && <Chip tone="green">Issued</Chip>}
      </div>
    </div>
  )
}

// How often the live tab re-reads the database while it is on screen. A minute
// is quick enough that a fix made in another tab is visible by the time the
// operator looks back, and slow enough that a screen left open all day costs
// one 0.1 s query a minute against a role capped at 15 connections. Hidden
// tabs do not poll; coming back to the tab refreshes at once.
const LIVE_REFRESH_MS = 60_000

// SAST, always: the database stores UTC and the operator reads Johannesburg.
function sastTime(iso: string): string {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('en-ZA', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Africa/Johannesburg',
  })
}

// `mode` picks which question the screen answers. 'statements' is the month as
// the owners' statements tell it, with the Figures menu choosing between what
// was sent and what it turned out to be. 'live' is the month as the database
// tells it right now: recomputed on every load, no statement adjustments, no
// balances or payouts, refreshed while it is open.
export function StatementMatrix({ mode = 'statements' }: { mode?: 'statements' | 'live' } = {}) {
  const live = mode === 'live'
  const matrixQuery = useGetStatementMatrix()
  const trigger = matrixQuery.trigger

  const [periodStart, setPeriodStart] = useState<string | null>(null)
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('sendable')
  const [search, setSearch] = useState('')
  // Defaults to what the month actually was. A correction found in September
  // that belongs to December is a December fact, and this screen is the
  // operator's view of the business rather than a copy of the document.
  const [basisChoice, setBasis] = useState<StatementBasis>('actual')
  const basis: StatementBasis = live ? 'live' : basisChoice
  // Date range, owner and vehicle, the same three the Performance tab offers.
  // Empty means "the selected month", so the screen opens exactly as it did.
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [ownerId, setOwnerId] = useState('')
  const [vehicleId, setVehicleId] = useState('')
  // Every line, including the ones this month is zero for. On by default: the
  // reason to look at this screen right now is to confirm the corrections
  // landed, and a line that is missing because it is empty is indistinguishable
  // from a line that is missing because it broke.
  const [showEmpty, setShowEmpty] = useState(true)

  const load = useCallback(
    () =>
      trigger(
        {
          ...(periodStart ? { period_start: periodStart } : {}),
          basis,
          from: from || null,
          to: to || null,
          owner_id: ownerId || null,
          vehicle_id: vehicleId || null,
        },
        // A cached live read would be the one thing this tab must never show.
        live ? { skipCache: true } : undefined,
      ),
    [trigger, periodStart, basis, from, to, ownerId, vehicleId, live],
  )

  useEffect(() => {
    load()
  }, [load])

  // Live only: re-read on a timer while visible, and at once when the operator
  // comes back to the tab, so a historical fix made elsewhere appears without
  // anybody pressing anything.
  useEffect(() => {
    if (!live) return
    const tick = () => {
      if (document.visibilityState === 'visible') load()
    }
    const id = window.setInterval(tick, LIVE_REFRESH_MS)
    document.addEventListener('visibilitychange', tick)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [live, load])

  const matrix = (matrixQuery.data as Matrix | undefined) ?? EMPTY_MATRIX
  const { periods, period, rows, cells, range } = matrix
  const isRange = (range?.periods_covered ?? 1) > 1

  // Columns the operator asked to see. The export deliberately ignores this and
  // always writes the whole month; see handleExport.
  const columns = useMemo(() => {
    const term = search.trim().toLowerCase()
    return matrix.columns.filter((c) => {
      if (groupFilter === 'sendable' && c.do_not_send) return false
      if (groupFilter !== 'sendable' && groupFilter !== 'all' && c.group_key !== groupFilter) {
        return false
      }
      if (!term) return true
      return `${c.plate} ${c.owner_name} ${c.owner_email} ${c.vehicle_name}`
        .toLowerCase()
        .includes(term)
    })
  }, [matrix.columns, groupFilter, search])

  // Rows that are entirely empty for the visible columns are dropped, so
  // filtering down to the seven new-model cars does not leave eight blank lines
  // for costs those cars do not carry.
  const visibleRows = useMemo(() => {
    if (columns.length === 0) return []
    if (showEmpty) return rows
    // Hide a line only when no visible column carries it AND none of them has a
    // non-zero value for it. A line present everywhere at R0.00 is still real.
    return rows.filter((row) =>
      columns.some((c) => {
        const cell = cells[c.statement_id]?.[row.line_code]
        if (cell === undefined) return false
        if (cell.p === false) return false
        return true
      }),
    )
  }, [rows, columns, cells, showEmpty])

  const payoutShown = useMemo(
    () => columns.reduce((sum, c) => sum + c.payout_amount_zar, 0),
    [columns],
  )

  // How much of this month was not known when the month was sent. This is the
  // number that answers "has anything changed", and it is shown whichever basis
  // is selected so the toggle is never the only way to find out.
  const corrections = useMemo(() => {
    const vehicles = columns.filter((c) => c.net_correction_zar !== 0)
    return {
      vehicles: vehicles.length,
      zar: vehicles.reduce((sum, c) => sum + c.net_correction_zar, 0),
    }
  }, [columns])

  const handleExport = useCallback(() => {
    if (!period) return
    const title = live ? `${periodShortLabel(period)}, live figures` : periodShortLabel(period)

    const sheets: XlsxSheet[] = GROUP_ORDER.map((group) => {
      const groupColumns = matrix.columns.filter((c) => c.group_key === group)
      // No em dash: Luyanda's standing formatting rule applies to anything
      // that leaves the building, and this workbook goes to the finance handover.
      const header = `Fleetly ${title}, ${GROUP_LABELS[group]} (${groupColumns.length} vehicle${
        groupColumns.length === 1 ? '' : 's'
      }).`
      const notSent = group === 'offboarded'

      const sheetRows: XlsxCell[][] = [
        [header],
        notSent ? ['These statements are NOT to be sent (offboarded/offboarding).'] : [],
        ['Line item', ...groupColumns.map((c) => c.plate)],
        ['Owner', ...groupColumns.map((c) => c.owner_name)],
      ]
      const boldRows = [0, 2]
      const countRows: number[] = []

      let lastSection: string | null = null
      for (const row of rows) {
        // Skip a line no vehicle in this group carries.
        if (!groupColumns.some((c) => cells[c.statement_id]?.[row.line_code])) continue
        if (row.section !== lastSection) {
          sheetRows.push([sectionHeading(row.section)])
          boldRows.push(sheetRows.length - 1)
          lastSection = row.section
        }
        const values: XlsxCell[] = groupColumns.map((c) => {
          const cell = cells[c.statement_id]?.[row.line_code]
          if (!cell || cell.e || cell.v === null) return null
          // The workbook has always shown Payouts Due as a positive amount.
          // Same convention on the screen and in the export, from the same
          // source, so the two can never drift apart.
          return displayValue(row, cell.v)
        })
        sheetRows.push([row.label, ...values])
        if (row.is_subtotal) boldRows.push(sheetRows.length - 1)
        if (!row.is_money) countRows.push(sheetRows.length - 1)
      }

      return {
        name: GROUP_LABELS[group],
        rows: sheetRows,
        boldRows,
        countRows,
        columnWidths: [30, ...groupColumns.map(() => 16)],
        freezeRows: 4,
        freezeCols: 1,
      }
    })

    downloadXlsx(`${periodFileStem(period)}${live ? '_live' : ''}.xlsx`, sheets)
  }, [matrix.columns, rows, cells, period, live])

  const loading = matrixQuery.loading

  // Live only. The net the database gives the visible vehicles, which is the
  // headline of this tab the way payouts due is the headline of the other.
  const liveNetShown = useMemo(
    () => columns.reduce((sum, c) => sum + (cells[c.statement_id]?.['net']?.v ?? 0), 0),
    [columns, cells],
  )

  // Live only: does the range reach back into the spreadsheet months, where
  // some cells are the imported statement's figures rather than a calculation.
  const reachesSpreadsheetMonths =
    live &&
    matrix.live_source_from !== null &&
    (range?.from ?? period?.period_start ?? '') < matrix.live_source_from

  // What is actually narrowing the view. The month is not counted: there is
  // always a month, so counting it would leave the badge permanently on and say
  // nothing. Sendable is the default and is not counted either.
  const activeFilters = useMemo(() => {
    let n = 0
    if (from) n += 1
    if (to) n += 1
    if (ownerId) n += 1
    if (vehicleId) n += 1
    if (groupFilter !== 'sendable') n += 1
    if (search.trim()) n += 1
    return n
  }, [from, to, ownerId, vehicleId, groupFilter, search])

  const filterSummary = useMemo(() => {
    const parts: string[] = []
    parts.push(
      isRange && range
        ? `${range.from} to ${range.to}`
        : period
          ? periodShortLabel(period)
          : 'no period',
    )
    if (ownerId) parts.push(matrix.owners.find((o) => o.owner_id === ownerId)?.owner_name ?? 'owner')
    if (vehicleId) parts.push(matrix.vehicles.find((v) => v.vehicle_id === vehicleId)?.plate ?? 'vehicle')
    if (search.trim()) parts.push(`"${search.trim()}"`)
    return parts.join(', ')
  }, [isRange, range, period, ownerId, vehicleId, matrix.owners, matrix.vehicles, search])

  return (
    // Radix throws "`Tooltip` must be used within `TooltipProvider`" without
    // this, and with no error boundary above it that throw unmounts the whole
    // app and the operator gets a blank white page. StatementTab wraps its own
    // provider the same way for StatementLines; this component is rendered
    // straight from the page, so it has to bring its own.
    <TooltipProvider delayDuration={150}>
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      {/* Controls. Collapsed behind one button on a phone; unchanged at sm and
          above. See components/common/FilterBar.tsx. */}
      <FilterBar
        activeCount={activeFilters}
        summary={filterSummary}
        trailing={`${columns.length} shown`}
      >
      <div className="flex flex-wrap items-end gap-3 px-4 py-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Month
          </span>
          <NativeSelect
            className="w-52"
            value={period?.period_start ?? ''}
            onChange={(e) => {
              setPeriodStart(e.target.value)
              // Picking a month is a way of saying "not a range".
              setFrom('')
              setTo('')
            }}
            disabled={periods.length === 0 || Boolean(from || to)}
          >
            {periods.map((p) => (
              <option key={p.period_start} value={p.period_start}>
                {periodShortLabel(p)}
                {p.draft_count > 0 ? ' (open)' : ''}
              </option>
            ))}
          </NativeSelect>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            From
          </span>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-[150px]"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            To
          </span>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-[150px]"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Owner
          </span>
          <NativeSelect
            className="w-48"
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
          >
            <option value="">All owners</option>
            {matrix.owners.map((o) => (
              <option key={o.owner_id} value={o.owner_id}>
                {o.owner_name}
              </option>
            ))}
          </NativeSelect>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Vehicle
          </span>
          <NativeSelect
            className="w-40"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
          >
            <option value="">All vehicles</option>
            {matrix.vehicles.map((v) => (
              <option key={v.vehicle_id} value={v.vehicle_id}>
                {v.plate}
              </option>
            ))}
          </NativeSelect>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Show
          </span>
          <NativeSelect
            className="w-60"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value as GroupFilter)}
          >
            {GROUP_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </NativeSelect>
        </label>

        {live ? (
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Figures
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" onClick={() => load()} disabled={loading} className="w-60 justify-start">
                  <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
                  Live{matrix.computed_at ? `, as of ${sastTime(matrix.computed_at)}` : ''}
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                Recomputed from shifts, costs, fines and the pay rules every time this loads, and
                again every minute while it is open. A change made to any earlier month shows here
                without anybody regenerating a statement. Statement adjustments, balances,
                retention and payouts are left out: those are how a statement settles, not what the
                month earned. Click to refresh now.
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Figures
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <NativeSelect
                className="w-60"
                value={basis}
                onChange={(e) => setBasis(e.target.value as StatementBasis)}
              >
                <option value="actual">Actual, in the month it happened</option>
                <option value="as_issued">As issued, what the owner was sent</option>
              </NativeSelect>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              An issued statement is never restated: a correction is settled forward onto a later
              one, and that is what the owner receives. On Actual the correction is shown against
              the month it belongs to instead, so December's mistake appears in December. Both
              numbers are on every corrected cell.
            </TooltipContent>
          </Tooltip>
        </label>
        )}

        <label className="flex min-w-[200px] flex-1 flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Find
          </span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Owner or plate"
              className="pl-9"
            />
          </div>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Lines
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <NativeSelect
                className="w-44"
                value={showEmpty ? 'all' : 'used'}
                onChange={(e) => setShowEmpty(e.target.value === 'all')}
              >
                <option value="all">Every line</option>
                <option value="used">Only lines in use</option>
              </NativeSelect>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              Every line shows all {rows.length} statement lines for every vehicle, including the
              ones that are zero. A line that is absent because the vehicle does not carry it is
              shown greyed, so an empty line and a broken one can be told apart.
            </TooltipContent>
          </Tooltip>
        </label>

        {(from || to || ownerId || vehicleId) && (
          <Button
            variant="ghost"
            onClick={() => {
              setFrom('')
              setTo('')
              setOwnerId('')
              setVehicleId('')
            }}
          >
            Clear filters
          </Button>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" onClick={handleExport} disabled={!period || loading}>
              <Download className="h-4 w-4" />
              Export
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Downloads the whole month as three sheets: new model, old model active, offboarded.
            The filters above do not change the export.
          </TooltipContent>
        </Tooltip>

        {loading && <Spinner className="mb-2 h-4 w-4 text-muted-foreground" />}
      </div>
      </FilterBar>

      {/* Summary. Context rather than the point of the screen, so it folds away
          on a phone and the figures start at the top. */}
      {period && (
        <CollapsibleBlock
          title="Month summary"
          trailing={live ? formatZar(liveNetShown) : formatZar(payoutShown)}
          className="border-b border-border bg-muted/40"
        >
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2 text-xs text-muted-foreground">
          <span>
            <span className="font-medium text-foreground">{columns.length}</span>{' '}
            {isRange ? 'vehicles' : 'of ' + period.statement_count + ' statements'} shown
          </span>
          {range && (
            <span>
              Period:{' '}
              <span className="font-medium text-foreground">
                {isRange ? `${range.from} to ${range.to}` : periodShortLabel(period)}
              </span>
              {isRange && (
                <span className="ml-1">
                  ({range.periods_covered} month{range.periods_covered === 1 ? '' : 's'})
                </span>
              )}
            </span>
          )}
          {live ? (
            <span>
              Net, shown:{' '}
              <span className="font-medium tabular-nums text-foreground">{formatZar(liveNetShown)}</span>
            </span>
          ) : (
            <>
              <span>
                Payouts due, shown:{' '}
                <span className="font-medium tabular-nums text-foreground">{formatZar(payoutShown)}</span>
              </span>
              <span>
                Payouts due, month:{' '}
                <span className="font-medium tabular-nums text-foreground">
                  {formatZar(period.payout_total_zar)}
                </span>
              </span>
            </>
          )}
          {live && corrections.vehicles > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default">
                  <Chip tone="neutral">
                    {corrections.vehicles} vehicle{corrections.vehicles === 1 ? '' : 's'} differ from
                    their statement: {formatZar(corrections.zar)} net
                  </Chip>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                How far the database has moved from what the statements say, net. Part of it is
                usually the statement adjustments this tab leaves out; the rest is shifts, costs or
                rulings that changed after the statement was drawn. Differing cells are marked;
                hover one to see both figures.
              </TooltipContent>
            </Tooltip>
          )}
          {!live && corrections.vehicles > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default">
                  <Chip tone={basis === 'actual' ? 'green' : 'amber'}>
                    {corrections.vehicles} vehicle{corrections.vehicles === 1 ? '' : 's'} corrected
                    since issue: {formatZar(corrections.zar)}
                    {basis === 'actual' ? ' (shown)' : ' (not shown)'}
                  </Chip>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                {basis === 'actual'
                  ? 'These figures include everything found after the statements went out that belongs to this month. Corrected cells are marked.'
                  : 'These figures are exactly what the owners were sent. Switch to Actual to see what the month turned out to be.'}
              </TooltipContent>
            </Tooltip>
          )}
          {period.draft_count > 0 && (
            <Chip tone="amber">
              {period.draft_count} draft{period.draft_count === 1 ? '' : 's'}: figures can still move
            </Chip>
          )}
          <span>Period ends {formatDate(period.period_end)}</span>
        </div>
        </CollapsibleBlock>
      )}

      {reachesSpreadsheetMonths && matrix.live_source_from && (
        <div className="flex items-start gap-2 border-b border-amber-300/60 bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/40 dark:text-amber-200">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Before {formatDate(matrix.live_source_from)} statements were built in the spreadsheets.
            Shift figures for those months are recomputed from the database, but insurance, Cartrack,
            maintenance, licensing, service, repairs, excess, idle petrol, leave and travel have no
            source in the database, so those cells are the imported statement's own figures. They
            are shown in italics.
          </span>
        </div>
      )}

      {/* Matrix */}
      <div className="min-h-0 flex-1 overflow-auto">
        {matrixQuery.error ? (
          <div className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{matrixQuery.error}</span>
          </div>
        ) : loading && matrix.columns.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-12 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading the month…
          </div>
        ) : columns.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            No statements match the current filters for this month.
          </div>
        ) : (
          <table className="w-max border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th
                  className="sticky left-0 top-0 z-30 border-b border-r border-border bg-card p-2 text-left align-bottom"
                  style={{ width: LABEL_W, minWidth: LABEL_W }}
                >
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Line item
                  </span>
                </th>
                <th
                  className="sticky top-0 z-30 border-b border-r border-border bg-card p-2 text-right align-bottom"
                  style={{ left: LABEL_W, width: TOTAL_W, minWidth: TOTAL_W }}
                >
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Shown total
                  </span>
                </th>
                {columns.map((column) => (
                  <th
                    key={column.statement_id}
                    className="sticky top-0 z-20 border-b border-r border-border bg-card p-2 text-left align-bottom"
                  >
                    <ColumnHeader column={column} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => {
                const previous = index > 0 ? visibleRows[index - 1] : undefined
                const startsSection = !previous || previous.section !== row.section
                const total = rowTotal(row, columns, cells)
                return (
                  <Fragment key={row.line_code}>
                    {startsSection && (
                      <tr>
                        <th
                          className="sticky left-0 z-20 border-b border-r border-border bg-muted/70 px-2 py-1 text-left"
                          style={{ width: LABEL_W, minWidth: LABEL_W }}
                        >
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {sectionHeading(row.section)}
                          </span>
                        </th>
                        <td
                          className="sticky z-20 border-b border-r border-border bg-muted/70"
                          style={{ left: LABEL_W, width: TOTAL_W, minWidth: TOTAL_W }}
                        />
                        {columns.map((column) => (
                          <td
                            key={column.statement_id}
                            className="border-b border-r border-border bg-muted/70"
                          />
                        ))}
                      </tr>
                    )}
                    <tr className="group">
                      <th
                        className={cn(
                          'sticky left-0 z-20 border-b border-r border-border bg-card px-2 py-1 text-left font-normal',
                          row.is_subtotal && 'font-bold',
                        )}
                        style={{ width: LABEL_W, minWidth: LABEL_W }}
                      >
                        <span className="block truncate text-foreground" title={row.description ?? row.label}>
                          {row.label}
                        </span>
                      </th>
                      <td
                        className={cn(
                          'sticky z-20 border-b border-r border-border bg-card px-2 py-1 text-right tabular-nums',
                          row.is_subtotal && 'font-bold',
                          total !== null &&
                            displayValue(row, total) < 0 &&
                            'text-rose-600 dark:text-rose-400',
                        )}
                        style={{ left: LABEL_W, width: TOTAL_W, minWidth: TOTAL_W }}
                      >
                        {hasFleetTotal(row) ? (
                          total === null ? (
                            <span className="text-muted-foreground">-</span>
                          ) : isSummable(row) ? (
                            formatCellValue(row, total)
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-default underline decoration-dotted underline-offset-2">
                                  {formatCellValue(row, total)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {DERIVED_TOTAL_HINT[row.line_code]}
                              </TooltipContent>
                            </Tooltip>
                          )
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-default text-muted-foreground">n/a</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              This line is a ratio. Adding it across vehicles would not mean
                              anything.
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </td>
                      {columns.map((column) => {
                        const cell = cells[column.statement_id]?.[row.line_code]
                        const negative =
                          cell?.v !== undefined && cell?.v !== null && displayValue(row, cell.v) < 0
                        // A cell that is not what the owner was sent says so on
                        // its face. Without this the two bases look identical
                        // and an operator reading the screen has no way to know
                        // which figure is in front of him.
                        const corrected = cell?.c !== undefined && cell.c !== 0
                        // The vehicle carries no such line. The value is a real
                        // zero, not a guess, but it is not a figure the
                        // statement asserted, so it does not read like one.
                        const absent = cell?.p === false
                        // Live only: the figure is the imported statement's,
                        // because the database has no source for it.
                        const fromStatement = live && cell?.s !== undefined
                        return (
                          <td
                            key={column.statement_id}
                            className={cn(
                              'border-b border-r border-border px-2 py-1 text-right tabular-nums',
                              row.is_subtotal && 'bg-muted/30 font-bold',
                              negative && 'text-rose-600 dark:text-rose-400',
                              corrected && 'bg-sky-50 dark:bg-sky-950/40',
                              absent && 'text-muted-foreground/45',
                              fromStatement && 'italic',
                            )}
                          >
                            {cell === undefined ? (
                              <span className="text-muted-foreground/50">·</span>
                            ) : cell.e ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex items-center gap-1 italic text-muted-foreground">
                                    <TriangleAlert className="h-3 w-3 text-amber-500" />
                                    n/a
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  {cell.n || 'This value could not be sourced for this period.'}
                                </TooltipContent>
                              </Tooltip>
                            ) : cell.v === null ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              // Every present figure is clickable, subtotals
                              // included: "which lines is this the sum of" is a
                              // question about a subtotal too. The provenance is
                              // fetched on open rather than with the matrix,
                              // because a 72 column month holds roughly two
                              // thousand cells and nobody opens two thousand.
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className="w-full rounded px-1 text-right tabular-nums underline decoration-dotted decoration-muted-foreground/40 underline-offset-4 hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    title={
                                      live
                                        ? [
                                            corrected
                                              ? `The statement says ${formatCellValue(row, cell.a ?? 0)}; the database now says ${formatCellValue(row, cell.v)}, a difference of ${formatCellValue(row, cell.c ?? 0)}.`
                                              : cell.s
                                                ? null
                                                : 'What backs this figure',
                                            cell.s,
                                          ]
                                            .filter(Boolean)
                                            .join(' ')
                                        : corrected
                                        ? `Corrected since issue. Sent as ${formatCellValue(
                                            row,
                                            cell.a ?? 0,
                                          )}, correction ${formatCellValue(row, cell.c ?? 0)}.${
                                            cell.w ? ` ${cell.w}` : ''
                                          }`
                                        : 'What backs this figure'
                                    }
                                  >
                                    {corrected && (
                                      <span
                                        aria-hidden
                                        className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-sky-500 align-middle"
                                      />
                                    )}
                                    {formatCellValue(row, cell.v)}
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-80 max-h-[70vh] overflow-y-auto">
                                  <StatementCellProvenance
                                    statementId={column.statement_id}
                                    lineCode={row.line_code}
                                    lineLabel={row.label}
                                    plate={column.plate}
                                    ownerName={column.owner_name}
                                    periodLabel={period?.period_label ?? ''}
                                  />
                                </PopoverContent>
                              </Popover>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* Corrections found after this month's statements went out. Below the
          matrix, never inside it: the matrix shows stored figures only, which is
          why it cannot disagree with the single-vehicle tab. A write is what we
          have since found to be true, and the two must stay separately readable. */}
      {period && !live && <MonthWrites periodStart={period.period_start} />}
    </div>
    </TooltipProvider>
  )
}
